/**
 * LanguageSwitcher - flag-based locale dropdown with scrolling
 * Shows the current language as a flag button and opens a scrollable dropdown
 * of all supported locale options using flag-icons.
 * @module components/ui/LanguageSwitcher
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import 'flag-icons/css/flag-icons.min.css';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  LANGUAGE_CONFIG,
  PUBLIC_LANGUAGE_OPTIONS,
} from '../../i18n';

// Mapping from language codes to country codes for flag-icons
const localeToCountry = {
  en: 'us',     // English → US
  de: 'de',     // Germany
  fr: 'fr',     // France
  it: 'it',     // Italy
  es: 'es',     // Spain
  pl: 'pl',     // Poland
  sv: 'se',     // Sweden
  nl: 'nl',     // Netherlands
  pt: 'pt',     // Portugal
  fi: 'fi',     // Finland
  da: 'dk',     // Denmark
  hu: 'hu',     // Hungary
  cs: 'cz',     // Czech Republic
  sk: 'sk',     // Slovakia
  sl: 'si',     // Slovenia
  et: 'ee',     // Estonia
  lv: 'lv',     // Latvia
  lt: 'lt',     // Lithuania
  hr: 'hr',     // Croatia
  ro: 'ro',     // Romania
  bg: 'bg',     // Bulgaria
  sw: 'ke',     // Swahili → Kenya
  zh: 'cn',     // Chinese → China
};

const buildPathWithLang = (pathname, newLang) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
    segments[0] = newLang;
  } else {
    segments.unshift(newLang);
  }
  return `/${segments.join('/')}`;
};

const getOptionList = (currentLang) => {
  const options = [...PUBLIC_LANGUAGE_OPTIONS];
  if (currentLang && SUPPORTED_LANGUAGES.includes(currentLang) && !options.includes(currentLang)) {
    options.unshift(currentLang);
  }
  return options.filter((code, index, arr) => arr.indexOf(code) === index);
};

const LanguageSwitcher = ({ variant = 'light', className = '' }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const currentLang = lang || i18n.language || DEFAULT_LANGUAGE;
  const currentConfig = LANGUAGE_CONFIG[currentLang] || LANGUAGE_CONFIG[DEFAULT_LANGUAGE];
  const options = useMemo(() => getOptionList(currentLang), [currentLang]);

  const handleChange = (newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) return;
    i18n.changeLanguage(newLang);
    navigate(`${buildPathWithLang(location.pathname, newLang)}${location.search}`, { replace: true });
    setOpen(false);
  };

  useEffect(() => {
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const isDark = variant === 'dark';
  const buttonClass = isDark
    ? 'inline-flex h-11 items-center gap-2 rounded-full border border-pearl-700 bg-white/5 px-3 text-sm font-medium text-white transition hover:border-gold-400 hover:bg-white/10'
    : 'inline-flex h-11 items-center gap-2 rounded-full border border-pearl-300 bg-white px-3 text-sm font-medium text-primary-900 shadow-soft transition hover:border-gold-400 hover:text-gold-600';

  const menuClass = isDark
    ? 'absolute left-0 top-full z-50 mt-3 min-w-56 max-h-80 overflow-y-auto rounded-3xl border border-pearl-700 bg-primary-950 p-2 shadow-elevated scrollbar-thin scrollbar-thumb-pearl-600 scrollbar-track-transparent'
    : 'absolute left-0 top-full z-50 mt-3 min-w-56 max-h-80 overflow-y-auto rounded-3xl border border-pearl-300 bg-white p-2 shadow-elevated scrollbar-thin scrollbar-thumb-pearl-300 scrollbar-track-transparent';

  const itemClass = isDark
    ? 'flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm text-pearl-100 transition hover:bg-white/10'
    : 'flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm text-primary-900 transition hover:bg-primary-50';

  const subTextClass = isDark ? 'text-pearl-400' : 'text-gray-400';

  return (
    <div ref={rootRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        className={buttonClass}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Current language: ${currentConfig.label}`}
      >
        <span className={`fi fi-${localeToCountry[currentLang]} text-lg leading-none`} aria-hidden="true"></span>
        <span className="hidden sm:inline">{currentConfig.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : 'rotate-0'}`} strokeWidth={1.9} />
      </button>

      {open && (
        <div role="menu" aria-label="Language options" className={menuClass}>
          <div className="px-3 pb-2 pt-1">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${subTextClass}`}>
              Current language
            </p>
            <p className={isDark ? 'mt-1 text-sm text-white' : 'mt-1 text-sm text-primary-900'}>
              <span className={`fi fi-${localeToCountry[currentLang]} mr-2`} aria-hidden="true"></span>
              {currentConfig.label}
            </p>
          </div>

          <div className="my-2 h-px bg-pearl-300/80" />

          <div className="space-y-1">
            {options.map((code) => {
              const config = LANGUAGE_CONFIG[code];
              const active = code === currentLang;

              return (
                <button
                  key={code}
                  type="button"
                  role="menuitem"
                  className={`${itemClass} ${active ? (isDark ? 'bg-white/10' : 'bg-primary-50') : ''}`}
                  onClick={() => handleChange(code)}
                >
                  <span className={`fi fi-${localeToCountry[code]} text-lg leading-none`} aria-hidden="true"></span>
                  <span className="flex-1">{config.label}</span>
                  {active && (
                    <span className={`text-xs font-semibold ${isDark ? 'text-gold-400' : 'text-primary-600'}`}>
                      Active
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;

// Developed and Designed by Trophy Developers
